import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, CheckCircle2 } from "lucide-react";

/**
 * SocialProofToast — live pop-up notifications in the bottom-left corner.
 *
 * Behavior:
 * - First notification appears ~15s after page load (avoids being intrusive).
 * - Each notification is visible ~6s, then fades out; next one appears after 20-35s.
 * - Random Italian PMI names + Tuscan/Italian cities + realistic actions.
 * - Can be manually dismissed and does NOT show again for that session (sessionStorage).
 * - Hidden on very small screens footer to avoid overlapping the WhatsApp button.
 */

// Pool of realistic Italian PMI/professional names.
// Includes the 6 real Google-verified reviewers plus additional credible names.
const NAMES = [
  // Real (Google verified)
  "Simona A.",
  "Roberto S.",
  "Germano B.",
  "Emanuele B.",
  "Massimiliano G.",
  // Additional realistic Italian PMI names
  "Marco R.",
  "Anna F.",
  "Luca B.",
  "Giulia R.",
  "Francesco C.",
  "Sara M.",
  "Paolo T.",
  "Chiara G.",
  "Andrea D.",
  "Elena C.",
  "Matteo F.",
  "Federica P.",
  "Alessandro V.",
  "Martina L.",
  "Davide N.",
  "Silvia B.",
  "Riccardo P.",
  "Valentina O.",
  "Fabio A.",
];

const CITIES = [
  "Pisa",
  "Lucca",
  "Firenze",
  "Livorno",
  "Massa",
  "Viareggio",
  "Pistoia",
  "Prato",
  "Milano",
  "Roma",
  "Bologna",
  "Torino",
  "Genova",
  "Verona",
  "Padova",
  "Modena",
  "Parma",
  "Brescia",
  "Bergamo",
];

// Each action generates a natural-sounding Italian sentence.
const ACTIONS = [
  { text: "ha appena completato il quiz", emoji: "✅" },
  { text: "ha appena completato il quiz", emoji: "✅" },
  { text: "ha appena completato il quiz", emoji: "✅" },
  { text: "si è iscritto/a al Corso Social Media Marketing", emoji: "🎓" },
  { text: "si è iscritto/a al Corso Agenti AI", emoji: "🤖" },
  { text: "si è iscritto/a al Corso Meta Ads Advanced", emoji: "📣" },
  { text: "si è iscritto/a all'esperienza All-In", emoji: "🚀" },
  { text: "ha prenotato una call di orientamento", emoji: "📞" },
  { text: "ha ricevuto il piano personalizzato via email", emoji: "📧" },
];

const MIN_TIME_MIN = 1;
const MAX_TIME_MIN = 32;

const randomOf = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randomTime = () =>
  Math.floor(Math.random() * (MAX_TIME_MIN - MIN_TIME_MIN + 1)) + MIN_TIME_MIN;

function makeNotification() {
  const name = randomOf(NAMES);
  const city = randomOf(CITIES);
  const action = randomOf(ACTIONS);
  const minutesAgo = randomTime();
  return {
    id: Date.now() + Math.random(),
    name,
    city,
    text: action.text,
    emoji: action.emoji,
    time: minutesAgo < 60 ? `${minutesAgo} min fa` : `1 ora fa`,
  };
}

const FIRST_DELAY_MS = 8000; // 8s after cookie decision (or fallback)
const COOKIE_FALLBACK_MS = 20000; // if user ignores cookie banner, still show after 20s
const VISIBLE_MS = 6500;
const MIN_GAP_MS = 15000;
const MAX_GAP_MS = 25000;
const SESSION_DISMISS_KEY = "mda_social_proof_dismissed";
const COOKIE_CONSENT_KEY = "mda_cookie_consent_v1";

// Wait until the user has made a cookie choice before showing the toast,
// otherwise it visually clashes with the cookie banner (same corner).
function hasCookieDecision() {
  try {
    return !!localStorage.getItem(COOKIE_CONSENT_KEY);
  } catch (_) {
    return true; // localStorage disabled -> assume no banner blocking
  }
}

export default function SocialProofToast() {
  const location = useLocation();
  const [current, setCurrent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [cookieBannerVisible, setCookieBannerVisible] = useState(!hasCookieDecision());
  const timersRef = useRef([]);

  // Hide on admin, checkout, and legal pages to avoid distraction
  const isHiddenRoute =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/checkout") ||
    location.pathname.startsWith("/privacy-policy") ||
    location.pathname.startsWith("/cookie-policy") ||
    location.pathname.startsWith("/termini-condizioni");

  // Poll cookie consent so the toast lifts above the banner as long as it's shown.
  useEffect(() => {
    if (!cookieBannerVisible) return undefined;
    const iv = setInterval(() => {
      if (hasCookieDecision()) {
        setCookieBannerVisible(false);
        clearInterval(iv);
      }
    }, 800);
    return () => clearInterval(iv);
  }, [cookieBannerVisible]);

  useEffect(() => {
    // Respect user dismissal for the session
    try {
      if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") {
        setDismissed(true);
        return;
      }
    } catch (_) {
      /* private mode: continue */
    }

    let stopped = false;

    const scheduleHide = () => {
      const t = setTimeout(() => setVisible(false), VISIBLE_MS);
      timersRef.current.push(t);
    };

    const scheduleNext = (delayMs) => {
      const t = setTimeout(() => {
        if (stopped) return;
        setCurrent(makeNotification());
        setVisible(true);
        scheduleHide();
        const gap =
          Math.floor(Math.random() * (MAX_GAP_MS - MIN_GAP_MS + 1)) +
          MIN_GAP_MS +
          VISIBLE_MS;
        scheduleNext(gap);
      }, delayMs);
      timersRef.current.push(t);
    };

    // Start showing toasts almost immediately regardless of cookie banner state
    // (the toast positions itself above the banner so they never overlap).
    scheduleNext(FIRST_DELAY_MS);

    return () => {
      stopped = true;
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, []);

  const handleClose = () => {
    setVisible(false);
    setDismissed(true);
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    } catch (_) {
      /* ignore */
    }
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const initials = useMemo(() => {
    if (!current) return "";
    return current.name
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }, [current]);

  if (dismissed || !current || isHiddenRoute || cookieBannerVisible) return null;

  // Always pinned to the bottom-left corner (shown only after the cookie
  // banner is gone, so the two never overlap and the toast never floats mid-page).
  const positionClass = "bottom-4 md:bottom-6";

  return (
    <div
      role="status"
      aria-live="polite"
      className={[
        // Visible on all screen sizes. WhatsApp float is bottom-RIGHT, we're bottom-LEFT: no overlap.
        "fixed z-40 left-3 right-3 sm:right-auto sm:left-4 sm:max-w-[340px]",
        positionClass,
        "transition-all duration-500 ease-out",
        visible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-3 pointer-events-none",
      ].join(" ")}
      data-testid="social-proof-toast"
    >
      <div className="relative rounded-2xl border border-white/10 bg-[#0e0e0e]/95 backdrop-blur-xl shadow-2xl shadow-black/40 px-4 py-3.5 pr-9">
        <div className="flex items-start gap-3">
          <div className="relative flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#d4ff3d]/30 to-white/5 border border-white/10 flex items-center justify-center">
              <span className="font-display text-white text-xs">{initials}</span>
            </div>
            <CheckCircle2
              size={14}
              className="absolute -bottom-0.5 -right-0.5 bg-[#0e0e0e] rounded-full"
              style={{ color: "#d4ff3d" }}
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-[13px] text-white/90 leading-tight">
              <span className="font-medium text-white">{current.name}</span>{" "}
              <span className="text-white/60">da {current.city}</span>
            </div>
            <div className="text-[12px] text-white/70 mt-0.5 leading-snug">
              {current.emoji} {current.text}
            </div>
            <div className="text-[10px] text-white/40 mt-1.5 font-mono uppercase tracking-wide">
              {current.time}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handleClose}
          aria-label="Chiudi notifica"
          className="absolute top-2.5 right-2.5 text-white/40 hover:text-white/80 transition-colors"
          data-testid="social-proof-close"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
